import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface SequenceStep {
  id: number
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
}

export interface Sequence {
  id: number
  title: string
  campaign_id: number
  bison_campaign_id: number // Note: This is now the same as the BisonCampaign.id
  steps: SequenceStep[]
  created_at: string
  updated_at: string
}

export interface CreateSequenceStepPayload {
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
}

export interface UpdateSequenceStepPayload {
  id?: number // Bison step ID - required for existing steps, omit for new steps
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
}

export interface CreateSequencePayload {
  campaign_id: number
  title: string
  steps: CreateSequenceStepPayload[]
}

export interface UpdateSequencePayload {
  title?: string
  steps?: UpdateSequenceStepPayload[]
}

// Query keys
export const sequenceKeys = {
  all: ['sequences'] as const,
  byCampaign: (campaignId: number) =>
    [...sequenceKeys.all, 'campaign', campaignId] as const,
}

// API calls
const getSequenceByCampaign = async (
  campaignId: number
): Promise<Sequence | null> => {
  const response = await baseAPICall<Sequence[]>(() =>
    clientInstance.get('/emailbison/sequences/', {
      params: { campaign_id: campaignId },
    })
  )
  // API returns array, but we expect at most one sequence per campaign
  return response.length > 0 ? response[0] : null
}

const createSequence = async (
  payload: CreateSequencePayload
): Promise<Sequence> => {
  return baseAPICall<Sequence>(() =>
    clientInstance.post('/emailbison/sequences/', payload)
  )
}

const updateSequence = async (
  sequenceId: number,
  payload: UpdateSequencePayload
): Promise<Sequence> => {
  return baseAPICall<Sequence>(() =>
    clientInstance.put(`/emailbison/sequences/${sequenceId}/`, payload)
  )
}

const deleteSequence = async (sequenceId: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/sequences/${sequenceId}/`)
  )
}

// Hooks
export const useGetCampaignSequence = (campaignId: number, enabled = true) => {
  return useQuery({
    queryKey: sequenceKeys.byCampaign(campaignId),
    queryFn: () => getSequenceByCampaign(campaignId),
    enabled: enabled && campaignId > 0,
  })
}

export const useCreateSequence = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSequence,
    onSuccess: (data) => {
      queryClient.setQueryData(sequenceKeys.byCampaign(data.campaign_id), data)
      // Invalidate campaign settings since creating a sequence also creates bison_campaign
      queryClient.invalidateQueries({
        queryKey: ['campaign-settings', 'campaign', data.campaign_id],
      })
    },
  })
}

export const useUpdateSequence = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sequenceId,
      payload,
    }: {
      sequenceId: number
      payload: UpdateSequencePayload
    }) => updateSequence(sequenceId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(sequenceKeys.byCampaign(campaignId), data)
    },
  })
}

export const useDeleteSequence = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSequence,
    onSuccess: () => {
      queryClient.setQueryData(sequenceKeys.byCampaign(campaignId), null)
    },
  })
}

// Lead types
export interface Lead {
  id: number
  email: string
  full_name: string
  first_name: string
  last_name: string
  verification_status: string
  emails_sent: number
  opens: number
  unique_opens: number
  replies: number
  unique_replies: number
}

export interface PaginatedLeadsResponse {
  count: number
  next: string | null
  previous: string | null
  results: Lead[]
}

export interface SyncLeadsResponse {
  data: Lead[]
  message: string
}

// Lead query keys
export const leadKeys = {
  all: ['leads'] as const,
  byBisonCampaignBase: (bisonCampaignId: number) =>
    [...leadKeys.all, 'bison_campaign', bisonCampaignId] as const,
  byBisonCampaign: (bisonCampaignId: number, page: number) =>
    [...leadKeys.byBisonCampaignBase(bisonCampaignId), 'page', page] as const,
}

// Lead API calls
const getCampaignLeads = async (
  bisonCampaignId: number,
  page: number = 1
): Promise<PaginatedLeadsResponse> => {
  const response = await baseAPICall<PaginatedLeadsResponse>(() =>
    clientInstance.get(`/emailbison/campaigns/${bisonCampaignId}/leads/`, {
      params: { page },
    })
  )
  return response
}

const syncCampaignLeads = async (
  bisonCampaignId: number
): Promise<SyncLeadsResponse> => {
  return baseAPICall<SyncLeadsResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/leads/sync/`)
  )
}

// Lead hooks
export const useGetCampaignLeads = (
  bisonCampaignId: number,
  page: number = 1
) => {
  return useQuery({
    queryKey: leadKeys.byBisonCampaign(bisonCampaignId, page),
    queryFn: () => getCampaignLeads(bisonCampaignId, page),
    enabled: bisonCampaignId > 0,
  })
}

export const useSyncCampaignLeads = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCampaignLeads(bisonCampaignId),
    onSuccess: () => {
      // Invalidate and refetch all pages of leads
      queryClient.invalidateQueries({
        queryKey: leadKeys.byBisonCampaignBase(bisonCampaignId),
      })
    },
  })
}

// ==================== Sender Email Types ====================
export interface SenderEmail {
  id: number
  name: string
  email: string
  email_signature: string
  account_type: string
  status: string
  daily_limit: number
  emails_sent: number
  opened: number
  unique_opens: number
  replied: number
  unique_replies: number
  bounced: number
  unsubscribed: number
  total_leads_contacted: number
  interested: number
  created_at: string
  updated_at: string
}

export interface SyncSenderEmailsResponse {
  data: SenderEmail[]
  created: number
  updated: number
}

export interface PaginatedSenderEmailsResponse {
  count: number
  next: string | null
  previous: string | null
  results: SenderEmail[]
}

export interface AttachSenderEmailsPayload {
  sender_email_ids: number[]
}

export interface AttachSenderEmailsResponse {
  attached_count: number | null
  skipped_count: number | null
  error_count: number | null
  errors: string[]
}

export interface UpdateSenderEmailPayload {
  name?: string
  daily_limit?: number
  email_signature?: string
}

// ==================== Sender Email Query Keys ====================
export const senderEmailKeys = {
  all: ['senderEmails'] as const,
  workspace: () => [...senderEmailKeys.all, 'workspace'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...senderEmailKeys.all, 'campaign', bisonCampaignId] as const,
}

// ==================== Sender Email API Calls ====================
const getWorkspaceSenderEmails =
  async (): Promise<PaginatedSenderEmailsResponse> => {
    return baseAPICall<PaginatedSenderEmailsResponse>(() =>
      clientInstance.get('/emailbison/sender-emails/')
    )
  }

const syncWorkspaceSenderEmails =
  async (): Promise<SyncSenderEmailsResponse> => {
    return baseAPICall<SyncSenderEmailsResponse>(() =>
      clientInstance.post('/emailbison/sender-emails/sync/')
    )
  }

const getCampaignSenderEmails = async (
  bisonCampaignId: number
): Promise<PaginatedSenderEmailsResponse> => {
  return baseAPICall<PaginatedSenderEmailsResponse>(() =>
    clientInstance.get(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/`
    )
  )
}

const attachCampaignSenderEmails = async (
  bisonCampaignId: number,
  payload: AttachSenderEmailsPayload
): Promise<AttachSenderEmailsResponse> => {
  return baseAPICall<AttachSenderEmailsResponse>(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/attach/`,
      payload
    )
  )
}

const detachCampaignSenderEmail = async (
  bisonCampaignId: number,
  senderEmailId: number
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(
      `/emailbison/campaigns/${bisonCampaignId}/sender-emails/${senderEmailId}/`
    )
  )
}

// ==================== Sender Email Hooks ====================
export const useGetWorkspaceSenderEmails = () => {
  return useQuery({
    queryKey: senderEmailKeys.workspace(),
    queryFn: getWorkspaceSenderEmails,
  })
}

export const useSyncWorkspaceSenderEmails = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: syncWorkspaceSenderEmails,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.workspace(),
      })
    },
  })
}

export const useGetCampaignSenderEmails = (bisonCampaignId: number) => {
  return useQuery({
    queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
    queryFn: () => getCampaignSenderEmails(bisonCampaignId),
    enabled: bisonCampaignId > 0,
  })
}

export const useAttachCampaignSenderEmails = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: AttachSenderEmailsPayload) =>
      attachCampaignSenderEmails(bisonCampaignId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

export const useDetachCampaignSenderEmail = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (senderEmailId: number) =>
      detachCampaignSenderEmail(bisonCampaignId, senderEmailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

// ==================== Update Sender Email ====================
const updateSenderEmail = async (
  senderEmailId: number,
  payload: UpdateSenderEmailPayload
): Promise<SenderEmail> => {
  return baseAPICall<SenderEmail>(() =>
    clientInstance.patch(`/emailbison/sender-emails/${senderEmailId}/`, payload)
  )
}

export const useUpdateSenderEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      senderEmailId,
      payload,
    }: {
      senderEmailId: number
      payload: UpdateSenderEmailPayload
    }) => updateSenderEmail(senderEmailId, payload),
    onSuccess: () => {
      // Invalidate workspace sender emails to refresh data
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.workspace(),
      })
    },
  })
}

// ==================== Delete Sender Email ====================
const deleteSenderEmail = async (senderEmailId: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/sender-emails/${senderEmailId}/`)
  )
}

export const useDeleteSenderEmail = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (senderEmailId: number) => deleteSenderEmail(senderEmailId),
    onSuccess: () => {
      // Invalidate workspace sender emails to refresh data
      queryClient.invalidateQueries({
        queryKey: senderEmailKeys.workspace(),
      })
    },
  })
}

// ==================== Scheduled Emails (Passthrough API) ====================
// NOTE: This is a PASSTHROUGH API - data is fetched directly from EmailBison API
// on each request. NOT from local database. Always fresh, never stale.

export interface ScheduledEmail {
  id: number
  email_subject: string | null
  email_body: string | null
  status: string | null
  scheduled_date: string | null
  scheduled_date_local: string | null
  sent_at: string | null
  thread_reply: boolean
  opens: number
  clicks: number
  replies: number
  unique_opens: number
  unique_replies: number
  interested: boolean
  // Flattened lead info
  lead_id: number | null
  lead_email: string | null
  lead_full_name: string
  lead_company: string | null
  // Flattened sender email info
  sender_email_id: number | null
  sender_email_address: string | null
  sender_email_name: string | null
}

export interface ScheduledEmailsResponse {
  count: number
  results: ScheduledEmail[]
}

// Scheduled emails query keys
export const scheduledEmailKeys = {
  all: ['scheduledEmails'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...scheduledEmailKeys.all, 'campaign', bisonCampaignId] as const,
}

// Scheduled emails API call (passthrough - fetches directly from EmailBison)
const getCampaignScheduledEmails = async (
  bisonCampaignId: number
): Promise<ScheduledEmailsResponse> => {
  return baseAPICall<ScheduledEmailsResponse>(() =>
    clientInstance.get(
      `/emailbison/campaigns/${bisonCampaignId}/scheduled-emails/`
    )
  )
}

// Scheduled emails hook
export const useGetCampaignScheduledEmails = (bisonCampaignId: number) => {
  return useQuery({
    queryKey: scheduledEmailKeys.byCampaign(bisonCampaignId),
    queryFn: () => getCampaignScheduledEmails(bisonCampaignId),
    enabled: bisonCampaignId > 0,
    // Shorter stale time since this is passthrough data that changes frequently
    staleTime: 30 * 1000, // 30 seconds
  })
}

// ==================== Campaign Status Operations ====================
export interface CampaignStatusResponse {
  status: string
}

// Campaign status query keys
export const campaignStatusKeys = {
  all: ['campaignStatus'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...campaignStatusKeys.all, bisonCampaignId] as const,
}

// API calls
const launchCampaign = async (
  bisonCampaignId: number
): Promise<CampaignStatusResponse> => {
  return baseAPICall<CampaignStatusResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/launch/`)
  )
}

const pauseCampaign = async (
  bisonCampaignId: number
): Promise<CampaignStatusResponse> => {
  return baseAPICall<CampaignStatusResponse>(() =>
    clientInstance.post(`/emailbison/campaigns/${bisonCampaignId}/pause/`)
  )
}

// Hooks
export const useLaunchCampaign = (
  bisonCampaignId: number,
  campaignId: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => launchCampaign(bisonCampaignId),
    onSuccess: () => {
      // Invalidate campaign settings to refetch with new status
      queryClient.invalidateQueries({
        queryKey: ['campaign-settings', 'campaign', campaignId],
      })
    },
  })
}

export const usePauseCampaign = (
  bisonCampaignId: number,
  campaignId: number
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => pauseCampaign(bisonCampaignId),
    onSuccess: () => {
      // Invalidate campaign settings to refetch with new status
      queryClient.invalidateQueries({
        queryKey: ['campaign-settings', 'campaign', campaignId],
      })
    },
  })
}

// ==================== Headless UI Token (Passthrough API) ====================
// Used for opening the EmailBison headless UI popup for adding email connections

export interface HeadlessUITokenResponse {
  token: string
}

const getHeadlessUIToken = async (): Promise<HeadlessUITokenResponse> => {
  return baseAPICall<HeadlessUITokenResponse>(() =>
    clientInstance.post('/emailbison/headless-ui-token/')
  )
}

export const useGetHeadlessUIToken = () => {
  return useMutation({
    mutationFn: getHeadlessUIToken,
  })
}

// ==================== AI Sequence Generation ====================

export interface AIGeneratedStep {
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
}

export interface AIGeneratedSequence {
  title: string
  steps: AIGeneratedStep[]
}

export interface GenerateAISequencePayload {
  campaign_id: number
  prompt: string
}

export interface SuggestedPrompt {
  label: string
  description: string
  prompt: string
}

// Query keys
export const aiSequenceKeys = {
  suggestions: ['ai-sequence-suggestions'] as const,
}

// API calls
const generateAISequence = async (
  payload: GenerateAISequencePayload
): Promise<AIGeneratedSequence> => {
  return baseAPICall<AIGeneratedSequence>(() =>
    clientInstance.post('/emailbison/sequences/generate-ai/', payload)
  )
}

const getAISuggestedPrompts = async (): Promise<SuggestedPrompt[]> => {
  return baseAPICall<SuggestedPrompt[]>(() =>
    clientInstance.get('/emailbison/sequences/generate-ai/suggestions/')
  )
}

// Hooks
export const useGenerateAISequence = () => {
  return useMutation({
    mutationFn: generateAISequence,
  })
}

export const useGetAISuggestedPrompts = () => {
  return useQuery({
    queryKey: aiSequenceKeys.suggestions,
    queryFn: getAISuggestedPrompts,
    staleTime: Infinity, // These don't change often
  })
}

// ==================== Campaign Replies ====================
// NOTE: Replies are synced via webhooks and stored locally.
// Manual sync available via sync endpoint.

export interface ReplyAttachment {
  id: number
  file_name: string
  download_url: string
}

export interface Reply {
  id: number
  folder: string
  subject: string | null
  html_body: string | null
  text_body: string | null
  is_read: boolean
  is_interested: boolean
  is_automated_reply: boolean
  is_tracked_reply: boolean
  date_received: string
  from_name: string | null
  from_email_address: string
  primary_to_email_address: string
  created_at: string
  updated_at: string
  // Flattened lead info
  lead_id: number | null
  lead_email: string | null
  lead_full_name: string | null
  // Flattened sender email info
  sender_email_id: number | null
  sender_email_address: string | null
  sender_email_name: string | null
  // Nested
  attachments: ReplyAttachment[]
}

export interface RepliesResponse {
  count: number
  results: Reply[]
}

export interface SyncRepliesResponse {
  synced: number
  created: number
  updated: number
  errors: number
}

// Reply query keys
export const replyKeys = {
  all: ['replies'] as const,
  byCampaign: (bisonCampaignId: number) =>
    [...replyKeys.all, 'campaign', bisonCampaignId] as const,
}

// Reply API calls
const getCampaignReplies = async (
  bisonCampaignId: number
): Promise<RepliesResponse> => {
  return baseAPICall<RepliesResponse>(() =>
    clientInstance.get(`/emailbison/campaigns/${bisonCampaignId}/replies/`)
  )
}

const syncCampaignReplies = async (
  bisonCampaignId: number
): Promise<SyncRepliesResponse> => {
  return baseAPICall<SyncRepliesResponse>(() =>
    clientInstance.post(
      `/emailbison/campaigns/${bisonCampaignId}/replies/sync/`
    )
  )
}

// Reply hooks
export const useGetCampaignReplies = (bisonCampaignId: number) => {
  return useQuery({
    queryKey: replyKeys.byCampaign(bisonCampaignId),
    queryFn: () => getCampaignReplies(bisonCampaignId),
    enabled: bisonCampaignId > 0,
  })
}

export const useSyncCampaignReplies = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCampaignReplies(bisonCampaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: replyKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}

// Mark reply as read API call
const markReplyRead = async (
  bisonCampaignId: number,
  replyId: number,
  isRead: boolean
): Promise<Reply> => {
  return baseAPICall<Reply>(() =>
    clientInstance.patch(
      `/emailbison/campaigns/${bisonCampaignId}/replies/${replyId}/`,
      { is_read: isRead }
    )
  )
}

// Mark reply as read mutation with optimistic updates
export const useMarkReplyRead = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ replyId, isRead }: { replyId: number; isRead: boolean }) =>
      markReplyRead(bisonCampaignId, replyId, isRead),

    onMutate: async ({ replyId, isRead }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: replyKeys.byCampaign(bisonCampaignId),
      })

      // Snapshot previous value
      const previousData = queryClient.getQueryData<RepliesResponse>(
        replyKeys.byCampaign(bisonCampaignId)
      )

      // Optimistically update
      if (previousData) {
        queryClient.setQueryData<RepliesResponse>(
          replyKeys.byCampaign(bisonCampaignId),
          {
            ...previousData,
            results: previousData.results.map((reply) =>
              reply.id === replyId ? { ...reply, is_read: isRead } : reply
            ),
          }
        )
      }

      return { previousData }
    },

    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          replyKeys.byCampaign(bisonCampaignId),
          context.previousData
        )
      }
    },

    onSettled: () => {
      // Refetch after mutation settles
      queryClient.invalidateQueries({
        queryKey: replyKeys.byCampaign(bisonCampaignId),
      })
    },
  })
}
